import { Controller } from '@hotwired/stimulus';
import { nearListEnd, spacesPageUrl } from '../services/spaces_menu/urls';

type SpacesKind = 'projects' | 'albums';
type SpacesTemplate = { name: string; background: string; color: string };
type SpacesItem = {
  id: number;
  name: string;
  url: string;
  go_to_menu_url?: string;
  template?: SpacesTemplate | null;
};
type SpacesPage = { items: SpacesItem[]; next_page: number | null };
type ListState = {
  nextPage: number | null;
  query: string;
  loading: boolean;
  failed: boolean;
  requestId: number;
};

const SEARCH_DELAY_MS = 250;

// The header's Spaces menu: lists the user's Workspaces (projects) and
// Portfolios (albums) a page at a time, searching and loading more over AJAX.
export default class extends Controller {
  static targets = ['popover', 'toggle', 'list', 'organisations'];
  static values = { projectsUrl: String, albumsUrl: String };

  declare readonly popoverTarget: HTMLElement;
  declare readonly toggleTarget: HTMLButtonElement;
  declare readonly listTargets: HTMLUListElement[];
  declare readonly organisationsTarget: HTMLElement;
  declare readonly projectsUrlValue: string;
  declare readonly albumsUrlValue: string;

  private states = new Map<SpacesKind, ListState>();
  private searchTimers = new Map<SpacesKind, number>();

  private openedWithPointer = false;

  open(event: MouseEvent): void {
    // A keyboard press on a button fires click with detail 0.
    this.openedWithPointer = event.detail > 0;
    // Load each list the first time, and retry any whose last request failed.
    for (const list of this.listTargets) {
      const kind = this.kindOf(list);
      const state = this.states.get(kind);
      if (!state || state.failed) this.reload(kind, state?.query ?? '');
    }
  }

  // Closing a popover hands focus back to its button, which Chrome then draws a
  // focus ring around. Keep that ring for keyboard users only.
  popoverToggled(event: ToggleEvent): void {
    if (event.newState === 'closed' && this.openedWithPointer) this.toggleTarget.blur();
  }

  search(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const kind = this.kindOf(input);

    window.clearTimeout(this.searchTimers.get(kind));
    this.searchTimers.set(
      kind,
      window.setTimeout(() => this.reload(kind, input.value), SEARCH_DELAY_MS),
    );
  }

  loadMore(event: Event): void {
    const list = event.currentTarget as HTMLUListElement;
    if (nearListEnd(list.scrollTop, list.clientHeight, list.scrollHeight))
      this.fetchPage(this.kindOf(list));
  }

  toggleOrganisations(): void {
    this.organisationsTarget.hidden = !this.organisationsTarget.hidden;
  }

  navigate(event: Event): void {
    event.preventDefault();
    const link = event.currentTarget as HTMLAnchorElement;

    this.popoverTarget.hidePopover();
    loadMainBody(link.getAttribute('href') ?? link.href);
  }

  // Swaps a project's cog for its Go to menu, fetched the first time the cog is clicked, and
  // opens it (CP-8929). The fragment keeps the menu's own scripts, which bind its Album(s) item.
  async openGoToMenu(event: Event): Promise<void> {
    const cog = event.currentTarget as HTMLButtonElement;
    if (cog.disabled) return;
    cog.disabled = true;

    try {
      const response = await fetch(cog.dataset.url ?? '', {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Go to menu request failed: ${response.status}`);

      const { html } = (await response.json()) as { html: string };
      const fragment = document.createRange().createContextualFragment(html);
      const menu = fragment.querySelector('.go-to-menu-wrapper');
      if (!menu) throw new Error('Go to menu missing from response');

      menu.classList.add('spaces-menu__go-to');
      cog.replaceWith(fragment);
      menu.querySelector<HTMLButtonElement>('.go-to-menu__toggle')?.click();
    } catch {
      cog.disabled = false;
      cog.title = 'The Go to menu could not be loaded. Click to try again.';
    }
  }

  // Choosing an item in a Go to menu leaves the Spaces menu, so it closes as the item's page,
  // popup or modal opens. Items that only expand a flyout keep it open.
  closeAfterGoToChoice(event: Event): void {
    const link = (event.target as Element).closest('.go-to-menu a');
    if (link && !link.matches('[aria-haspopup]')) this.popoverTarget.hidePopover();
  }

  private reload(kind: SpacesKind, query: string): void {
    const previous = this.states.get(kind);
    this.states.set(kind, {
      nextPage: 1,
      query,
      loading: false,
      failed: false,
      requestId: (previous?.requestId ?? 0) + 1,
    });
    this.listFor(kind).replaceChildren(
      this.buildStatusMessage(query.trim() ? 'Searching...' : 'Preparing your menu...'),
    );
    this.fetchPage(kind);
  }

  private async fetchPage(kind: SpacesKind): Promise<void> {
    const state = this.states.get(kind);
    if (!state || state.loading || state.nextPage === null) return;

    state.loading = true;
    const requestId = state.requestId;
    const list = this.listFor(kind);
    list.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(spacesPageUrl(this.urlFor(kind), state.nextPage, state.query), {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Spaces menu request failed: ${response.status}`);

      const page = (await response.json()) as SpacesPage;
      if (requestId !== this.states.get(kind)?.requestId) return;

      this.clearStatusMessage(list);
      list.append(...page.items.map((item) => this.buildItem(item)));
      state.nextPage = page.next_page;
      if (list.childElementCount === 0) list.append(this.buildEmptyMessage(kind, state.query));
    } catch {
      if (requestId === this.states.get(kind)?.requestId) {
        state.nextPage = null;
        state.failed = true;
        this.clearStatusMessage(list);
        list.append(
          this.buildStatusMessage(
            'Your menu could not be loaded. Close Spaces and open it again to retry.',
          ),
        );
      }
    } finally {
      state.loading = false;
      list.removeAttribute('aria-busy');
    }
  }

  private buildItem(item: SpacesItem): HTMLLIElement {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = item.url;
    link.textContent = item.name;
    if (item.template) link.append(' ', this.buildTemplateLabel(item.template));
    link.className = 'spaces-menu__item';
    link.dataset.skipAjaxLoading = 'true';
    link.dataset.action = 'click->spaces-menu#navigate';
    li.append(link);
    if (item.go_to_menu_url) li.append(this.buildGoToMenuCog(item.name, item.go_to_menu_url));
    return li;
  }

  private buildGoToMenuCog(name: string, url: string): HTMLButtonElement {
    const cog = document.createElement('button');
    cog.type = 'button';
    cog.className = 'spaces-menu__project-cog';
    cog.title = 'Go to';
    cog.setAttribute('aria-label', `Go to menu for ${name}`);
    cog.dataset.url = url;
    cog.dataset.action = 'click->spaces-menu#openGoToMenu';

    const icon = document.createElement('i');
    icon.className = 'fa fa-cog';
    icon.setAttribute('aria-hidden', 'true');
    cog.append(icon);
    return cog;
  }

  // The project's Project Template, so projects of different kinds can be told apart (CP-8831).
  private buildTemplateLabel(template: SpacesTemplate): HTMLSpanElement {
    const label = document.createElement('span');
    label.className = 'spaces-menu__template-label';
    label.textContent = template.name;
    label.title = `Project Template: ${template.name}`;
    label.style.backgroundColor = template.background;
    label.style.color = template.color;
    return label;
  }

  private buildStatusMessage(text: string): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'spaces-menu__empty spaces-menu__status';
    li.textContent = text;
    return li;
  }

  private clearStatusMessage(list: HTMLUListElement): void {
    list.querySelector('.spaces-menu__status')?.remove();
  }

  private buildEmptyMessage(kind: SpacesKind, query: string): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'spaces-menu__empty';
    const noun = kind === 'projects' ? 'workspaces' : 'portfolios';
    li.textContent = query.trim() ? `No ${noun} match your search` : `You have no ${noun} yet`;
    return li;
  }

  private kindOf(element: HTMLElement): SpacesKind {
    return element.dataset.kind === 'albums' ? 'albums' : 'projects';
  }

  private listFor(kind: SpacesKind): HTMLUListElement {
    return this.listTargets.find((list) => this.kindOf(list) === kind) as HTMLUListElement;
  }

  private urlFor(kind: SpacesKind): string {
    return kind === 'projects' ? this.projectsUrlValue : this.albumsUrlValue;
  }
}
